import { useEffect, useState } from 'react';
import { usePrefs } from '../lib/prefs.js';
import { api } from '../lib/api.js';
import {
  COLOR_META, SURVEY, scoreSurvey,
  type ColorKey, type PersonalitySurveyResult,
} from '../lib/colorSurvey.js';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (result: PersonalitySurveyResult) => void;
}

export function ColorSurvey({ open, onClose, onSaved }: Props) {
  const [prefs] = usePrefs();
  const rows = SURVEY[prefs.language] ?? SURVEY.fr;

  const [rankings, setRankings] = useState<ColorKey[][]>(() => rows.map(() => []));
  const [step, setStep] = useState(0);
  const [result, setResult] = useState<PersonalitySurveyResult | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRankings(rows.map(() => []));
    setStep(0);
    setResult(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, rows, onClose]);

  if (!open) return null;

  const row = rows[step]!;
  const ranking = rankings[step] ?? [];
  const isFinalRow = step === rows.length - 1;
  const allRanked = rankings.every((r) => r.length === 4);

  function setRow(next: ColorKey[]) {
    const nextAll = rankings.slice(); nextAll[step] = next; setRankings(nextAll);
  }
  function pick(color: ColorKey) {
    if (ranking.includes(color)) {
      // Click an already-ranked card → remove just that one; others renumber.
      setRow(ranking.filter((c) => c !== color));
      return;
    }
    if (ranking.length >= 4) return;
    const next = [...ranking, color];
    setRow(next);
    if (next.length === 4 && !isFinalRow) setStep((s) => s + 1);
  }
  function moveUp(color: ColorKey) {
    const i = ranking.indexOf(color);
    if (i <= 0) return;
    const next = ranking.slice();
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    setRow(next);
  }
  function moveDown(color: ColorKey) {
    const i = ranking.indexOf(color);
    if (i < 0 || i >= ranking.length - 1) return;
    const next = ranking.slice();
    [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
    setRow(next);
  }
  function resetRow() { setRow([]); }

  function compute() {
    setResult(scoreSurvey(rankings));
  }

  async function save() {
    if (!result) return;
    setSaving(true);
    try {
      // Merge into the existing persona (PUT shallow-merges via savePersona on the server).
      const cur = await api<{ persona: Record<string, unknown> }>('/api/persona');
      await api('/api/persona', {
        method: 'PUT',
        body: JSON.stringify({ persona: { ...cur.persona, personalitySurvey: result } }),
      });
      onSaved(result);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/65 backdrop-blur-md grid place-items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-[min(720px,94vw)] max-h-[88vh] overflow-y-auto bg-void border border-ink/15 rounded-2xl p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl text-ink m-0">Color survey</h3>
            <p className="text-[11.5px] text-ink/40 mt-1">
              True Colors · {result ? 'Result' : `Row ${step + 1} of ${rows.length}`}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>
        </div>

        {!result ? (
          <>
            {/* Stepper */}
            <div className="flex gap-1.5">
              {rows.map((_, i) => (
                <button key={i} onClick={() => setStep(i)}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    i === step ? 'bg-amber' : rankings[i]?.length === 4 ? 'bg-ink/40' : 'bg-ink/10'
                  }`} />
              ))}
            </div>

            <div>
              <div className="flex items-end justify-between mb-1">
                <p className="font-serif italic text-[15px] text-ink/55">{row.theme}</p>
                {ranking.length > 0 && (
                  <button onClick={resetRow} className="text-[11px] text-ink/40 hover:text-ink/70">clear</button>
                )}
              </div>
              <p className="text-[11.5px] text-ink/40 mb-3">
                Click in order — 1st is most like you, 4th is least. Re-order with ▲▼. {ranking.length}/4
              </p>

              {/* Unranked pool first */}
              {row.items.some((it) => !ranking.includes(it.color)) && (
                <ul className="space-y-2 mb-3">
                  {row.items.filter((it) => !ranking.includes(it.color)).map((it) => {
                    const meta = COLOR_META[it.color];
                    return (
                      <li key={it.color}>
                        <button onClick={() => pick(it.color)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-ink/10 hover:border-ink/30 text-left transition-colors">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: meta.hex }} />
                          <span className="flex-1 font-serif text-[15px] text-ink">{it.text}</span>
                          <span className="text-[11px] text-ink/35">tap to rank</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Ranked, in order */}
              {ranking.length > 0 && (
                <ol className="space-y-2">
                  {ranking.map((color, i) => {
                    const it = row.items.find((x) => x.color === color)!;
                    const meta = COLOR_META[color];
                    return (
                      <li key={color}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-amber bg-amber/[0.05]">
                        <span className="font-mono text-[11px] tracking-wider px-2 py-0.5 rounded-full bg-amber text-void flex-shrink-0">#{i + 1}</span>
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: meta.hex }} />
                        <span className="flex-1 font-serif text-[15px] text-ink">{it.text}</span>
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => moveUp(color)} disabled={i === 0} title="Move up"
                            className="w-7 h-7 rounded-full border border-ink/15 grid place-items-center text-ink/55 hover:text-ink disabled:opacity-20">▲</button>
                          <button onClick={() => moveDown(color)} disabled={i === ranking.length - 1} title="Move down"
                            className="w-7 h-7 rounded-full border border-ink/15 grid place-items-center text-ink/55 hover:text-ink disabled:opacity-20">▼</button>
                          <button onClick={() => pick(color)} title="Remove ranking"
                            className="w-7 h-7 rounded-full border border-ink/15 grid place-items-center text-ink/45 hover:text-red-300">✕</button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-ink/10">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="px-4 py-2 rounded-full border border-ink/15 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-30">← Back</button>
              <button onClick={() => setStep((s) => Math.min(rows.length - 1, s + 1))} disabled={ranking.length < 4 || isFinalRow}
                className="px-4 py-2 rounded-full border border-ink/15 text-[11.5px] text-ink/60 hover:text-ink disabled:opacity-30">Next →</button>
              <button onClick={compute} disabled={!allRanked}
                className="ml-auto px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
                See result
              </button>
            </div>
          </>
        ) : (
          <ResultPanel result={result} onRetake={() => { setResult(null); setStep(0); setRankings(rows.map(() => [])); }} onSave={save} saving={saving} />
        )}
      </div>
    </div>
  );
}

function ResultPanel({ result, onRetake, onSave, saving }: { result: PersonalitySurveyResult; onRetake: () => void; onSave: () => void; saving: boolean }) {
  const primary = COLOR_META[result.primary];
  const secondary = COLOR_META[result.secondary];
  const order: ColorKey[] = ['blue', 'gold', 'green', 'orange'];
  return (
    <>
      <div className="rounded-2xl p-5 border border-ink/10" style={{ background: `${primary.hex}1a` }}>
        <p className="text-[11px] text-ink/45">Primary</p>
        <p className="font-serif text-3xl text-ink mt-1" style={{ color: primary.hex }}>{primary.label}</p>
        <p className="font-serif italic text-ink/70 mt-1">{primary.tagline}</p>
        <p className="font-mono text-[10px] tracking-wider text-ink/45 mt-2">
          {result.percents[result.primary]}% · Secondary: {secondary.label} ({result.percents[result.secondary]}%)
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {order.map((k) => {
          const m = COLOR_META[k];
          const pct = result.percents[k];
          return (
            <div key={k} className="flex items-center gap-3">
              <span className="w-16 text-[11.5px] text-ink/55">{m.label}</span>
              <div className="flex-1 h-3 rounded-full bg-ink/10 overflow-hidden">
                <div className="h-full" style={{ width: `${pct}%`, background: m.hex }} />
              </div>
              <span className="w-10 text-right font-mono text-[11px] text-ink/60 tabular-nums">{pct}%</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 pt-3 border-t border-ink/10">
        <button onClick={onRetake}
          className="px-4 py-2 rounded-full border border-ink/15 text-[11.5px] text-ink/60 hover:text-ink">Retake</button>
        <button onClick={onSave} disabled={saving}
          className="ml-auto px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-40">
          {saving ? 'Saving…' : 'Save to persona'}
        </button>
      </div>
    </>
  );
}
